import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Sparkles, Loader2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

export default function CoachInsights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/insights/portfolio");
        setData(res.data || null);
      } catch (err) {
        console.warn("Failed to load coach insights:", err.message);
      }
      setLoading(false);
    })();
  }, []);

  async function generate() {
    setGenerating(true);
    try {
      const { data } = await api.post("/insights/portfolio");
      setData({ ...data, updated_at: new Date().toISOString() });
      toast.success("Insights refreshed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setGenerating(false);
    }
  }

  const fmt = (s) => {
    if (!s) return "";
    try { return new Date(s).toLocaleString("en-IN", { hour12: false }); } catch { return s; }
  };

  return (
    <div className="bg-[#121318] border border-[#27272A] rounded-sm" data-testid="coach-insights">
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#27272A]">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 bg-[#FF9900]/10 border border-[#FF9900]/30 flex items-center justify-center rounded-sm">
            <Sparkles className="h-3.5 w-3.5 text-[#FF9900]" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#FF9900] font-semibold">Coach Insights</div>
            <div className="text-[10px] text-[#71717A] num">
              {data?.updated_at ? `Updated ${fmt(data.updated_at)}` : "Not generated yet"}
              {data?.trade_count > 0 && (
                <span data-testid="coach-insight-scope">
                  {` · ${data.trade_count} trade${data.trade_count === 1 ? "" : "s"} closed ${data.period === "recent" ? "(10 most recent — none in last 7 days)" : "in the last 7 days"}`}
                </span>
              )}
            </div>
          </div>
        </div>
        <Button
          data-testid="generate-insights-btn"
          size="sm"
          onClick={generate}
          disabled={generating}
          className="bg-[#FF9900] hover:bg-[#FF9900]/90 text-black rounded-sm h-8 text-[10px] tracking-wider font-semibold"
        >
          {generating ? <><Loader2 className="h-3 w-3 animate-spin mr-2" /> Analyzing…</> : <><RefreshCcw className="h-3 w-3 mr-2" /> {data?.insight ? "Refresh" : "Generate"}</>}
        </Button>
      </div>
      <div className="px-5 py-4">
        {loading ? (
          <div className="text-xs text-[#71717A]">Loading…</div>
        ) : data?.insight ? (
          <div data-testid="coach-insight-text" className="text-sm text-[#F8F9FA] whitespace-pre-wrap leading-relaxed">{data.insight}</div>
        ) : (
          <div className="text-xs text-[#71717A]">
            Close at least 1 trade and click <span className="text-[#FF9900]">Generate</span> to receive AI-powered coaching on your last week's closed trades — strengths, recurring mistakes, emotional patterns, and concrete rules for next week.
          </div>
        )}
      </div>
    </div>
  );
}
